import helperTexts from '../helperTexts.json'
import errorMessages from '../errorMessages.json'
import { SocketAcceptorHostPort } from './customFields/SocketAcceptorHostPort'
import { SocketConnectHostPort } from './customFields/SocketConnectHostPort'
import { MessagesHandlerSelect } from './customFields/MessagesHandlerSelect'
import { SessionType } from './SessionType'
import { EnableSearching } from './customFields/EnableSearching'
import { senderTargetCompIdRegex } from './formUtils'
import { SessionMessageProviderSelect } from './customFields/SessionMessageProviderSelect'

export const getAddSessionFormSchema = (
    editSession,
    isActive,
    sessionType,
    advanced,
    fixVersion,
    fixVersionHandlers,
    sessionMessageProviders,
    getValues,
    sessions) => [
    {
        name: 'Session',
        type: 'section',
        rows: [
            {
                type: 'line',
                style: {
                    padding: 3,
                },
                content: [
                    {
                        label: 'Sender Comp ID',
                        name: 'senderCompID',
                        helperText: helperTexts.senderCompID,
                        isDisabled: editSession?.senderCompID,
                        type: 'string',
                        required: true,
                        validate: (value) => {
                            if (!value.match(senderTargetCompIdRegex)) {
                                return errorMessages.senderTargetCompId.incorrectFormat
                            } else if (!editSession) {
                                const sessionID = `${value}:${getValues('targetCompID')}`
                                if (sessions.some(session => session.sessionID === sessionID)) {
                                    return `Session ${sessionID} already exists!`
                                }
                            }
                            return true
                        },
                        style: {
                            padding: 3,
                        },
                    },
                    {
                        label: 'Sender Sub ID',
                        name: 'senderSubID',
                        helperText: helperTexts.senderSubID,
                        isDisabled: editSession?.senderSubID,
                        type: 'string',
                        required: false,
                        style: {
                            padding: 3,
                        },
                    },
                    {
                        label: 'Target Comp ID',
                        name: 'targetCompID',
                        helperText: helperTexts.targetCompID,
                        isDisabled: editSession?.targetCompID,
                        type: 'string',
                        required: true,
                        validate: (value) => {
                            if (!value.match(senderTargetCompIdRegex)) {
                                return errorMessages.senderTargetCompId.incorrectFormat
                            } else if (!editSession) {
                                const sessionID = `${getValues('senderCompID')}:${value}`
                                if (sessions.some(session => session.sessionID === sessionID)) {
                                    return false
                                }
                            }
                            return true
                        },
                        style: {
                            padding: 3,
                        },
                    }
                ]
            }
        ]
    },
    {
        name: 'Connection Type',
        type: 'section',
        rows: [
            {
                type: 'line',
                content: [
                    {
                        label: 'Socket Acceptor Host:Port',
                        type: 'custom',
                        render: SocketAcceptorHostPort,
                        isDisabled: isActive
                    },
                    {
                        label: 'Socket Connect Host:Port',
                        type: 'custom',
                        render: SocketConnectHostPort,
                        isDisabled: isActive
                    }
                ]
            },
        ]
    },
    {
        type: 'section',
        advanced: advanced,
        rows: [
            {
                type: 'line',
                content: [
                    {
                        label: 'Session Message Provider',
                        name: 'sessionMessageProvider',
                        messageHandlerClasses: sessionMessageProviders,
                        helperText: helperTexts.sessionMessageProvider,
                        type: 'custom',
                        render: SessionMessageProviderSelect,
                        fullWidth: true,
                        disabled: isActive
                    }
                ]
            }
        ]
    },
    {
        type: 'section',
        rows: [
            {
                type: 'line',
                style: {
                    marginBottom: 3,
                },
                content: [
                    {
                        label: 'FIX Version',
                        name: 'fixVersion',
                        helperText: helperTexts.fixVersion,
                        type: 'select',
                        required: true,
                        values: Object.keys(fixVersionHandlers),
                        style: {
                            width: '674px',
                            display: 'flex',
                            alignItems: 'baseline'
                        },
                        inputBackground: 'var(--chakra-colors-input)',
                        isDisabled: isActive
                    }
                ]
            },
            {
                type: 'line',
                content: [
                    {
                        label: 'Message Parser',
                        name: 'messageParser',
                        messageHandlerClasses: fixVersionHandlers[fixVersion].messageParsers,
                        helperText: helperTexts.messageParser,
                        type: 'custom',
                        render: MessagesHandlerSelect,
                        fullWidth: true,
                        required: true,
                        disabled: isActive || sessionType !== SessionType.Simple,
                        disabledTooltip: isActive ? '' : 'Router sessions should use RouterMessageParser'
                    }
                ]
            },
            {
                type: 'line',
                content: [
                    {
                        label: 'Message Generator',
                        name: 'messageGenerator',
                        messageHandlerClasses: fixVersionHandlers[fixVersion].messageGenerators,
                        helperText: helperTexts.messageGenerator,
                        type: 'custom',
                        render: MessagesHandlerSelect,
                        fullWidth: true,
                        required: true,
                        disabled: isActive
                    }
                ]
            },
            {
                type: 'line',
                content: [
                    {
                        label: 'Message Notifier',
                        name: 'messageNotifier',
                        messageHandlerClasses: fixVersionHandlers[fixVersion].messageNotifiers,
                        helperText: helperTexts.messageNotifier,
                        type: 'custom',
                        render: MessagesHandlerSelect,
                        fullWidth: true,
                        required: true,
                        disabled: isActive
                    }
                ]
            }
        ]
    },
    ...(advanced
        ? [
              {
                  name: 'Advanced',
                  type: 'container',
                  rows: [
                      {
                          type: 'line',
                          justify: true,
                          content: [
                              {
                                  type: 'container',
                                  name: '',
                                  rows: [
                                      {
                                          type: 'column',
                                          content: [
                                              {
                                                  type: 'section',
                                                  half: true,
                                                  style: {
                                                    height: '100%'
                                                  },
                                                  rows: [
                                                      {
                                                          type: 'column',
                                                          style: {
                                                            height: '100%'
                                                          },
                                                          content: [
                                                              {
                                                                  label: 'File Store Path',
                                                                  name: 'fileStorePath',
                                                                  helperText: helperTexts.fileStorePath,
                                                                  type: 'string',
                                                                  isDisabled: isActive
                                                              },
                                                              {
                                                                  label: 'Heart Beat Interval',
                                                                  name: 'heartBtInt',
                                                                  helperText: helperTexts.heartBtInt,
                                                                  type: 'number',
                                                                  isDisabled: isActive
                                                              }
                                                          ]
                                                      },
                                                  ]
                                              },
                                              {
                                                type: 'section',
                                                half: true,
                                                rows: [
                                                    {
                                                        type: 'column',
                                                        content: [
                                                            {
                                                                label: 'Enable FIX Messages Searching',
                                                                type: 'custom',
                                                                isDisabled: isActive,
                                                                render: EnableSearching
                                                            }
                                                        ]
                                                    },
                                                ]
                                            }
                                          ]
                                      }
                                  ]
                              },
                              {
                                  type: 'container',
                                  name: '',
                                  rows: [
                                      {
                                          type: 'column',
                                          content: [
                                              {
                                                  type: 'section',
                                                  half: true,
                                                  name: 'Reset',
                                                  rows: [
                                                      {
                                                          type: 'column',
                                                          content: [
                                                              {
                                                                  label: 'Reset On Logon Strategy',
                                                                  name: 'resetOnLogonStrategy',
                                                                  values: ['NEVER', 'ALWAYS'],
                                                                  helperText: helperTexts.resetOnLogonStrategy,
                                                                  type: 'select',
                                                                  required: false,
                                                                  inputBackground: 'var(--chakra-colors-input)',
                                                                  disabled: isActive
                                                              },
                                                              {
                                                                  label: 'At Session End',
                                                                  name: 'resetOnSessionEnd',
                                                                  helperText: helperTexts.resetOnSessionEnd,
                                                                  type: 'boolean',
                                                                  isDisabled: isActive
                                                              },
                                                              {
                                                                  label: 'At Session Start',
                                                                  name: 'resetOnSessionStart',
                                                                  helperText: helperTexts.resetOnSessionStart,
                                                                  type: 'boolean',
                                                                  isDisabled: isActive
                                                              },
                                                              {
                                                                  label: 'On Schedule',
                                                                  name: 'resetOnSchedule',
                                                                  helperText: helperTexts.ResetOnSchedule,
                                                                  type: 'boolean',
                                                                  isDisabled: isActive
                                                              }
                                                          ]
                                                      }
                                                  ]
                                              },
                                              {
                                                  type: 'section',
                                                  half: true,
                                                  rows: [
                                                      {
                                                          type: 'column',
                                                          content: [
                                                              {
                                                                  label: 'Send Gap Fill For Resend Request',
                                                                  name: 'sendGapFillForResendRequest',
                                                                  helperText: helperTexts.SendGapFillForResendRequest,
                                                                  type: 'boolean',
                                                                  isDisabled: isActive
                                                              }
                                                          ]
                                                      }
                                                  ]
                                              }
                                          ]
                                      }
                                  ]
                              }
                          ]
                      }
                  ]
              }
          ]
        : [])
]
